// Client-side junk-email guard for marketing forms. Catches disposable
// mailboxes, shared-inbox prefixes, and common typos so the HubSpot Lead
// pipeline doesn't fill up with addresses Theo/SDRs have no way to reach.
// This is layer 1 of email quality — purely format/list checks, no MX or
// SMTP probing. Server-side verification (Layer B) is a future addition
// and would live in formOnWritten, not here.

export type EmailRejectionReason =
  | "format"
  | "non_ascii"
  | "disposable"
  | "role_based";

export type EmailValidationResult =
  | { ok: true; suggestion?: string }
  | { ok: false; reason: EmailRejectionReason; suggestion?: string };

// Maps a rejection reason to the i18n key under the `DemoForm` namespace.
// Kept here so all forms surface the same copy for the same condition.
export const EMAIL_REASON_KEYS: Record<EmailRejectionReason, string> = {
  format: "emailErrorInvalid",
  non_ascii: "emailErrorInvalidChars",
  disposable: "emailErrorDisposable",
  role_based: "emailErrorRoleBased"
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NON_ASCII_REGEX = /[^\x00-\x7F]/;

// Curated list of the most commonly abused disposable / temporary mail
// providers. Not exhaustive — comprehensive coverage (~3k domains) is
// available via the `disposable-email-domains` npm package if needed.
const DISPOSABLE_DOMAINS = new Set<string>([
  "0815.ru",
  "10minutemail.com",
  "10minutemail.net",
  "20minutemail.com",
  "30minutemail.com",
  "anonbox.net",
  "armyspy.com",
  "aufeminin.com",
  "binkmail.com",
  "bobmail.info",
  "burnermail.io",
  "byom.de",
  "chacuo.net",
  "cuvox.de",
  "deadaddress.com",
  "dharmatel.net",
  "dingbone.com",
  "discard.email",
  "discardmail.com",
  "discardmail.de",
  "dispostable.com",
  "dodgit.com",
  "dropmail.me",
  "duck.com",
  "duckmail.com",
  "easytrashmail.com",
  "emailfake.com",
  "emailisvalid.com",
  "emailondeck.com",
  "emltmp.com",
  "fakeinbox.com",
  "fakemail.fr",
  "fakemail.net",
  "fakemailgenerator.net",
  "fakermail.com",
  "fastmail.fm",
  "fleckens.hu",
  "gawab.com",
  "getairmail.com",
  "getnada.com",
  "grr.la",
  "guerrillamail.biz",
  "guerrillamail.com",
  "guerrillamail.de",
  "guerrillamail.info",
  "guerrillamail.net",
  "guerrillamail.org",
  "guerrillamailblock.com",
  "harakirimail.com",
  "imgof.com",
  "inboxalias.com",
  "inboxbear.com",
  "inboxkitten.com",
  "incognitomail.com",
  "instantmailaddress.com",
  "jetable.org",
  "kasmail.com",
  "luxusmail.org",
  "mail-temporaire.fr",
  "mail.tm",
  "mail7.io",
  "mailcatch.com",
  "maildrop.cc",
  "mailexpire.com",
  "mailforspam.com",
  "mailinator.com",
  "mailinator.net",
  "mailinator2.com",
  "mailnesia.com",
  "mailpoof.com",
  "mailsac.com",
  "mailtothis.com",
  "meltmail.com",
  "mintemail.com",
  "mohmal.com",
  "moot.es",
  "moakt.com",
  "muellmail.com",
  "mvrht.net",
  "mytemp.email",
  "mytrashmail.com",
  "nada.email",
  "nada.ltd",
  "no-spam.ws",
  "noclickemail.com",
  "nomail.xl.cx",
  "notmailinator.com",
  "nowmymail.com",
  "objectmail.com",
  "onewaymail.com",
  "outlawspam.com",
  "owlpic.com",
  "pokemail.net",
  "proxymail.eu",
  "quickinbox.com",
  "rcpt.at",
  "rmqkr.net",
  "rppkn.com",
  "safetymail.info",
  "sharklasers.com",
  "shieldedmail.com",
  "shortmail.net",
  "skeefmail.com",
  "slopsbox.com",
  "smashmail.de",
  "soodonims.com",
  "spam4.me",
  "spamavert.com",
  "spambog.com",
  "spambog.de",
  "spamdecoy.net",
  "spamex.com",
  "spamfree24.de",
  "spamfree24.org",
  "spamgourmet.com",
  "spamhereplease.com",
  "spaml.com",
  "spaml.de",
  "spamslicer.com",
  "spamthis.co.uk",
  "spoofmail.de",
  "superrito.com",
  "tafmail.com",
  "tempail.com",
  "tempemail.com",
  "tempemail.net",
  "tempinbox.com",
  "tempmail.com",
  "tempmail.de",
  "tempmail.eu",
  "tempmail.ninja",
  "tempmail.plus",
  "tempmail.us",
  "tempmail.us.com",
  "tempmailaddress.com",
  "tempmaili.com",
  "tempmailo.com",
  "temp-mail.io",
  "temp-mail.org",
  "tempr.email",
  "throwam.com",
  "throwawaymail.com",
  "tmail.ws",
  "tmpeml.com",
  "tmpmail.net",
  "tmpmail.org",
  "trashmail.at",
  "trashmail.com",
  "trashmail.de",
  "trashmail.net",
  "trashmail.ws",
  "trbvm.com",
  "trillianpro.com",
  "vomoto.com",
  "vpn.st",
  "vsimcard.com",
  "wegwerfemail.de",
  "wegwerfmail.de",
  "wegwerfmail.net",
  "wegwerfmail.org",
  "wegwerpmailadres.nl",
  "wuzup.net",
  "wuzupmail.net",
  "yopmail.com",
  "yopmail.fr",
  "yopmail.net",
  "zetmail.com"
]);

// Shared-inbox prefixes — addresses that almost certainly don't reach a
// decision-maker. We compare against the local-part before any plus-tag.
const ROLE_BASED_PREFIXES = new Set<string>([
  "abuse",
  "accounts",
  "accounting",
  "admin",
  "administrator",
  "billing",
  "careers",
  "ceo",
  "contact",
  "cto",
  "email",
  "feedback",
  "finance",
  "help",
  "hello",
  "hi",
  "hr",
  "info",
  "inquiry",
  "inquiries",
  "invoices",
  "it",
  "jobs",
  "legal",
  "mail",
  "marketing",
  "noreply",
  "no-reply",
  "office",
  "postmaster",
  "press",
  "privacy",
  "sales",
  "security",
  "support",
  "team",
  "webmaster"
]);

// Domains where a typo would still produce a plausible-looking address —
// the user likely meant one of these.
const COMMON_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "hotmail.com",
  "hotmail.co.uk",
  "hotmail.de",
  "hotmail.fr",
  "hotmail.se",
  "outlook.com",
  "outlook.co.uk",
  "outlook.de",
  "outlook.fr",
  "outlook.se",
  "live.com",
  "live.se",
  "live.de",
  "live.co.uk",
  "yahoo.com",
  "yahoo.co.uk",
  "yahoo.de",
  "yahoo.fr",
  "yahoo.se",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "protonmail.com",
  "proton.me",
  "mail.com",
  "yandex.com",
  "yandex.ru",
  "gmx.com",
  "gmx.net",
  "gmx.de",
  "web.de",
  "t-online.de",
  "telia.com",
  "spray.se",
  "bredband.net",
  "comhem.se",
  "tele2.se"
];

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev = new Array<number>(b.length + 1);
  const curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

function suggestDomain(domain: string): string | undefined {
  if (COMMON_DOMAINS.includes(domain)) return undefined;
  let best: { domain: string; distance: number } | undefined;
  for (const candidate of COMMON_DOMAINS) {
    const distance = levenshtein(domain, candidate);
    if (distance === 0) return undefined;
    if (distance <= 2 && (!best || distance < best.distance)) {
      best = { domain: candidate, distance };
    }
  }
  return best?.domain;
}

export function validateEmail(email: string): EmailValidationResult {
  if (!EMAIL_REGEX.test(email)) return { ok: false, reason: "format" };
  if (NON_ASCII_REGEX.test(email)) return { ok: false, reason: "non_ascii" };

  const [localRaw, domainRaw] = email.split("@");
  const domain = domainRaw.toLowerCase();
  const localPart = localRaw.toLowerCase().split("+")[0];

  if (ROLE_BASED_PREFIXES.has(localPart)) {
    return { ok: false, reason: "role_based" };
  }
  if (DISPOSABLE_DOMAINS.has(domain)) {
    return { ok: false, reason: "disposable" };
  }

  const domainSuggestion = suggestDomain(domain);
  if (domainSuggestion) {
    return { ok: true, suggestion: `${localRaw}@${domainSuggestion}` };
  }
  return { ok: true };
}
